import { useEffect, useRef, useState } from 'react';
import { Button, Stack, Text } from '@mantine/core';
import { coordinateNumber, isValidCoordinates } from 'utils/timezones';
import { getSchoolLocation, SchoolLocation } from 'utils/schoolLocation';

type Props = {
  latitude: unknown;
  longitude: unknown;
  enabled: boolean;
  onResolved: (location: SchoolLocation) => void;
};

export const SchoolLocationAutofill = ({
  latitude,
  longitude,
  enabled,
  onResolved,
}: Props) => {
  const lat = coordinateNumber(latitude);
  const lng = coordinateNumber(longitude);
  const location = `${lat},${lng}`;
  const callback = useRef(onResolved);
  callback.current = onResolved;
  const [retry, setRetry] = useState(0);
  const [lookup, setLookup] = useState<{ location: string; error?: string }>();

  useEffect(() => {
    if (!enabled || !isValidCoordinates(lat, lng)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void getSchoolLocation(lat, lng).then(
        (result) => {
          if (cancelled) return;
          callback.current(result);
          setLookup({ location });
        },
        (error: unknown) => {
          if (!cancelled)
            setLookup({
              location,
              error:
                error instanceof Error
                  ? error.message
                  : 'Could not look up this location.',
            });
        },
      );
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lat, lng, location, enabled, retry]);

  if (!enabled || !isValidCoordinates(lat, lng)) return null;
  const current = lookup?.location === location ? lookup : undefined;
  if (current && !current.error) return null;
  return (
    <Stack spacing="xs">
      <Text size="sm" role="status" color={current?.error ? 'red' : 'dimmed'}>
        {current?.error || 'Looking up location…'}
      </Text>
      {current?.error && (
        <Button
          variant="subtle"
          onClick={() => {
            setLookup(undefined);
            setRetry((value) => value + 1);
          }}
        >
          Retry location lookup
        </Button>
      )}
    </Stack>
  );
};
